// useVerlassenSperre.ts — hält Wege aus einer Seite mit ungespeicherten
// Änderungen an, bis die Seite gefragt hat (Kontaktakte, Befund 28.09.2026).
//
// Drei Wege, drei Mittel:
//
//   1. In-App-Navigation (Menü, Links, `navigate(...)`, auch `navigate(-1)`):
//      Die App läuft auf dem deklarativen `<BrowserRouter>` — `useBlocker`
//      gibt es dort nicht (nur mit Data-Router). Deshalb werden `push`,
//      `replace` und `go` des Router-Navigators für die Dauer der Sperre
//      umgelenkt: erst fragen, dann (vielleicht) weiter.
//   2. Browser-Zurück/-Vor: kommt als `popstate`, NACHDEM die URL schon
//      gewechselt hat. Ein Capture-Listener am `window` läuft vor dem des
//      Routers, hält das Ereignis an und springt per `history.go(-delta)`
//      zurück; nach „Weiter" geht es mit `history.go(delta)` doch dorthin.
//   3. Tab schließen / neu laden: `beforeunload`. Den Text bestimmt der
//      Browser — ein eigener Dialog ist dort technisch nicht möglich.
//
// Maßgeblich ist bei jedem Versuch `ungespeichert()` im Moment des Versuchs.
// Ist nichts offen, läuft alles wie immer — kein Dialog, kein Umweg.

import * as React from "react";
import { UNSAFE_NavigationContext, useLocation } from "react-router";

type Ziel = string | { pathname?: string; search?: string; hash?: string };

const historyIdx = (): number => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" ? idx : 0;
};

/**
 * Verlässt `ziel` die aktuelle Seite? Reine Zustands-/Hash-Wechsel auf
 * derselben Adresse (z. B. Scroll-Wiederherstellung) nicht; ein anderer
 * Pfad oder andere Suchparameter (`?tab=…` wechselt den Reiter) schon.
 */
export function verlaesstSeite(ziel: Ziel, aktuell: { pathname: string; search: string }): boolean {
  let pfad: string;
  let suche: string;
  if (typeof ziel === "string") {
    const u = new URL(ziel, `http://x${aktuell.pathname}${aktuell.search}`);
    pfad = u.pathname;
    suche = u.search;
  } else {
    pfad = ziel.pathname ?? aktuell.pathname;
    suche = ziel.search ?? (ziel.pathname == null ? aktuell.search : "");
  }
  return pfad !== aktuell.pathname || (suche || "") !== (aktuell.search || "");
}

export function useVerlassenSperre(opts: {
  /** In-App-Navigation und Browser-Zurück abfangen (Vollseite, nicht im Fenster). */
  navigation: boolean;
  /** Gibt es JETZT Ungespeichertes? (synchron — `beforeunload` wartet nicht) */
  ungespeichert: () => boolean;
  /** Fragt nach und speichert/verwirft; `true` = weiter. */
  verlassenErlaubt: () => Promise<boolean>;
}): void {
  const { navigator } = React.useContext(UNSAFE_NavigationContext);
  const location = useLocation();

  const ungespeichertRef = React.useRef(opts.ungespeichert);
  ungespeichertRef.current = opts.ungespeichert;
  const erlaubtRef = React.useRef(opts.verlassenErlaubt);
  erlaubtRef.current = opts.verlassenErlaubt;
  const ortRef = React.useRef({ pathname: location.pathname, search: location.search });
  ortRef.current = { pathname: location.pathname, search: location.search };

  /** Der nächste `popstate` stammt von uns selbst (Weiter bzw. Rücksprung). */
  const durchlassenRef = React.useRef(false);
  const ruecksprungRef = React.useRef(false);
  const idxRef = React.useRef(0);
  React.useEffect(() => {
    idxRef.current = historyIdx();
  }, [location.key]);

  const abfangen = React.useCallback((weiter: () => void) => {
    if (!ungespeichertRef.current()) {
      weiter();
      return;
    }
    void erlaubtRef.current().then((ok) => {
      if (ok) weiter();
    });
  }, []);

  // (1) Navigator umlenken.
  React.useEffect(() => {
    if (!opts.navigation) return;
    const nav = navigator as unknown as {
      push: (to: Ziel, ...rest: unknown[]) => void;
      replace: (to: Ziel, ...rest: unknown[]) => void;
      go: (delta: number) => void;
    };
    const { push, replace, go } = nav;
    nav.push = (to, ...rest) => {
      const weiter = () => push.call(nav, to, ...rest);
      if (!verlaesstSeite(to, ortRef.current)) return weiter();
      abfangen(weiter);
    };
    nav.replace = (to, ...rest) => {
      const weiter = () => replace.call(nav, to, ...rest);
      if (!verlaesstSeite(to, ortRef.current)) return weiter();
      abfangen(weiter);
    };
    nav.go = (delta) => {
      if (!delta) return go.call(nav, delta);
      abfangen(() => {
        durchlassenRef.current = true;
        go.call(nav, delta);
      });
    };
    return () => {
      nav.push = push;
      nav.replace = replace;
      nav.go = go;
    };
  }, [navigator, opts.navigation, abfangen]);

  // (2) Browser-Zurück/-Vor.
  React.useEffect(() => {
    if (!opts.navigation) return;
    const onPop = (e: PopStateEvent) => {
      const neu = historyIdx();
      if (durchlassenRef.current) {
        durchlassenRef.current = false;
        return;
      }
      if (ruecksprungRef.current) {
        // Unser eigener Rücksprung — der Router soll davon nichts merken.
        ruecksprungRef.current = false;
        e.stopImmediatePropagation();
        return;
      }
      if (!ungespeichertRef.current()) return;
      const delta = neu - idxRef.current;
      if (!delta) return;
      e.stopImmediatePropagation();
      ruecksprungRef.current = true;
      window.history.go(-delta);
      void erlaubtRef.current().then((ok) => {
        if (!ok) return;
        durchlassenRef.current = true;
        window.history.go(delta);
      });
    };
    window.addEventListener("popstate", onPop, true);
    return () => window.removeEventListener("popstate", onPop, true);
  }, [opts.navigation]);

  // (3) Tab schließen / neu laden.
  React.useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!ungespeichertRef.current()) return undefined;
      e.preventDefault();
      // Ältere Browser zeigen den Dialog nur mit gesetztem returnValue.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);
}
